import React, { useState, useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import CommonTable from '../../../../common/table/ComonTable';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UserList = () => {
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filters
    const [statusFilter, setStatusFilter] = useState("");
    const [selectedWing, setSelectedWing] = useState("");
    const [selectedFloor, setSelectedFloor] = useState("");
    
    const [pageInfo, setPageInfo] = useState({
        pageNumber: 1,
        pageSize: 10,
        totalPages: 1,
        totalRecords: 0
    });

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await apiServices({
                method: "GET",
                url: `/ResidentialUser/GetAllResidentialUsers`
            });

            if (response.isSuccess || response.success || response.IsSuccess || response) {
                let apiData = response.data || response;
                let data = [];
                if (Array.isArray(apiData)) {
                    data = apiData;
                } else if (apiData && Array.isArray(apiData.data)) {
                    data = apiData.data;
                } else if (apiData && Array.isArray(apiData.ResidentialUserData)) {
                    data = apiData.ResidentialUserData;
                }

                // Sort descending by userId
                data.sort((a, b) => (b.userId || b.UserId) - (a.userId || a.UserId));

                setUsers(data);
                setPageInfo(prev => ({
                    ...prev,
                    totalRecords: data.length,
                    totalPages: Math.ceil(data.length / prev.pageSize)
                }));
            }
        } catch (error) {
            console.error("Error fetching residential users:", error);
            toast.error("Failed to fetch users.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleLimitChange = (newLimit) => {
        setPageInfo(prev => ({ ...prev, pageSize: newLimit, pageNumber: 1 }));
        setCurrentPage(1);
    };

    const handleDelete = async (row) => {
        if (!window.confirm("Are you sure you want to delete this user?")) return;
        try {
            const id = row.userId || row.UserId;
            // Assuming this is the correct endpoint for deletion, many modules use status updates instead
            const res = await apiServices({
                url: `/ResidentialUser/DeleteResidentialUser/${id}`,
                method: 'DELETE'
            });
            if (res.success || res.isSuccess) {
                toast.success("User deleted successfully");
                fetchUsers();
            } else {
                toast.error("Failed to delete user");
            }
        } catch (error) {
            console.error("Error deleting user:", error);
            toast.error("An error occurred while deleting");
        }
    };

    const handleEdit = (row) => {
        const id = row.userId || row.UserId;
        navigate(`/zentres/Family-management/familyform/${id}`, { state: { id, item: row } });
    };

    const tableHeaders = [
        { label: "No", key: "no", align: "left" },
        { label: "Image", key: "image", align: "center" },
        { label: "Name", key: "name", align: "left" },
        { label: "Mobile Number", key: "mobileNumber", align: "left" },
        { label: "Gender", key: "gender", align: "left" },
        { label: "Role", key: "roleName", align: "left" },
        { label: "Wing", key: "wingName", align: "left" },
        { label: "Floor", key: "floorName", align: "left" },
        { label: "Flat", key: "unitName", align: "left" },
        { label: "Active Status", key: "status", align: "center" },
        { label: "", key: "actions", align: "center" }
    ];

    const renderCell = (key, row, index) => {
        const val = (k) => row[k] || row[k.charAt(0).toUpperCase() + k.slice(1)] || "—";
        switch (key) {
            case "no":
                return <span className="text-[13px] font-medium text-[#64748B]">{index + 1 + (currentPage - 1) * pageInfo.pageSize}</span>;
            case "image":
                const img = row.userImage || row.UserImage;
                const fNameImg = row.firstName || row.FirstName || "";
                const lNameImg = row.lastName || row.LastName || "";
                return (
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center mx-auto border border-gray-200">
                        {img ? (
                            <img src={img} alt="User" className="w-full h-full object-cover" />
                        ) : (
                            <span className="text-gray-400 font-bold text-[13px]">{(fNameImg.charAt(0) || '') + (lNameImg.charAt(0) || '')}</span>
                        )}
                    </div>
                );
            case "name":
                const firstName = row.firstName || row.FirstName || "";
                const lastName = row.lastName || row.LastName || "";
                return <span className="text-[13px] font-medium text-gray-800 tracking-tight">{firstName} {lastName}</span>;
            case "mobileNumber":
            case "gender":
            case "roleName":
            case "wingName":
            case "floorName":
                return <span className="text-[13px] font-medium text-[#64748B]">{val(key)}</span>;
            case "unitName":
                const uName = row.unitName || row.UnitName || "";
                const uNum = row.unitNumber || row.UnitNumber || "";
                const displayUnit = (uName && uNum) ? `${uName}(${uNum})` : (uName || uNum || "—");
                return <span className="text-[13px] font-medium text-[#64748B]">{displayUnit}</span>;
            case "status":
                const status = row.status || row.Status || row.isActive || row.IsActive || (row.isActiveId === 1 ? 'Active' : 'Inactive') || 'Active';
                let statusColor = "bg-gray-100 text-gray-600";
                if (status === 'Active') statusColor = "bg-[#DCFCE7] text-[#166534]";
                if (status === 'Inactive') statusColor = "bg-[#FEF9C3] text-[#D97706]";
                return (
                    <span className={`px-3.5 py-1.5 rounded-full text-[12px] font-semibold shadow-sm ${statusColor}`}>
                        {status}
                    </span>
                );
            case "actions":
                return null; // Handled by CommonTable
            default:
                return <span className="text-xs font-medium text-[#64748B]">{row[key] || "—"}</span>;
        }
    };

    // Derived Lists for Filters
    const wingList = useMemo(() => {
        const wings = users.map(u => u.wingName || u.WingName).filter(Boolean);
        return [...new Set(wings)];
    }, [users]);

    const floorList = useMemo(() => {
        const floors = users.map(u => u.floorName || u.FloorName).filter(Boolean);
        return [...new Set(floors)];
    }, [users]);

    const filteredData = useMemo(() => {
        let data = users;
        if (statusFilter) {
            data = data.filter(item => {
                const s = item.status || item.Status || item.isActive || item.IsActive || (item.isActiveId === 1 ? 'Active' : 'Inactive');
                return s === statusFilter;
            });
        }
        if (selectedWing) {
            data = data.filter(item => (item.wingName || item.WingName) === selectedWing);
        }
        if (selectedFloor) {
            data = data.filter(item => (item.floorName || item.FloorName) === selectedFloor);
        }
        return data;
    }, [users, statusFilter, selectedWing, selectedFloor]);

    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageInfo.pageSize;
        return filteredData.slice(start, start + pageInfo.pageSize);
    }, [filteredData, currentPage, pageInfo.pageSize]);

    const headerAction = (
        <div className="flex flex-col sm:flex-row items-center gap-3">
            <select
                value={selectedWing}
                onChange={(e) => {
                    setSelectedWing(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Wings</option>
                {wingList.map((wing, i) => (
                    <option key={i} value={wing}>{wing}</option>
                ))}
            </select>

            <select
                value={selectedFloor}
                onChange={(e) => {
                    setSelectedFloor(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Floors</option>
                {floorList.map((floor, i) => (
                    <option key={i} value={floor}>{floor}</option>
                ))}
            </select>

            <select
                value={statusFilter}
                onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                }}
                className="w-full sm:w-40 px-3 py-2 border border-gray-200 bg-[#F8FAFC] rounded-lg text-xs font-medium focus:outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
            </select>
        </div>
    );

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto gap-6 pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="User Management"
                subHeading="Manage users (Family, Owners) for residential units"
                primaryBtnText="Create User"
                primaryBtnIcon={faPlus}
                PrimaryOnClick={() => navigate('/zentres/Family-management/familyform/add')}
            />

            <div className="shrink-0 flex-1 flex flex-col">
                <CommonTable
                    tableHeading="Users List"
                    headerAction={headerAction}
                    headers={tableHeaders}
                    data={paginatedData.map((d, i) => ({ ...d, originalIndex: i }))}
                    renderCell={(key, row) => renderCell(key, row, row.originalIndex)}
                    pagination={{
                        ...pageInfo,
                        pageNumber: currentPage,
                        totalRecords: filteredData.length,
                        totalPages: Math.ceil(filteredData.length / pageInfo.pageSize) || 1
                    }}
                    onPageChange={setCurrentPage}
                    onLimitChange={handleLimitChange}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                />
            </div>
        </div>
    );
};

export default UserList;
